import { FC, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { AI_QUICK_EDIT_CONTACT } from './gql';

export interface CandidateQuickEditModalProps {
  isOpen: boolean;
  candidateId: string;
  fullName: string | null | undefined;
  email: string | null | undefined;
  phone: string | null | undefined;
  currentCompany: string | null | undefined;
  currentTitle: string | null | undefined;
  onClose: () => void;
  /** Called after a successful save so the parent can refetch the console. */
  onSaved: () => void;
}

const splitName = (
  full: string | null | undefined,
): { first: string; last: string } => {
  const parts = (full ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
};

const CandidateQuickEditModal: FC<CandidateQuickEditModalProps> = ({
  isOpen,
  candidateId,
  fullName,
  email,
  phone,
  currentCompany,
  currentTitle,
  onClose,
  onSaved,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [emailVal, setEmailVal] = useState('');
  const [phoneVal, setPhoneVal] = useState('');
  const [company, setCompany] = useState('');
  const [title, setTitle] = useState('');
  const [save, { loading }] = useMutation(AI_QUICK_EDIT_CONTACT);

  // Seed the form whenever the modal opens.
  useEffect(() => {
    if (isOpen) {
      const { first, last } = splitName(fullName);
      setFirstName(first);
      setLastName(last);
      setEmailVal(email ?? '');
      setPhoneVal(phone ?? '');
      setCompany(currentCompany ?? '');
      setTitle(currentTitle ?? '');
    }
  }, [isOpen, fullName, email, phone, currentCompany, currentTitle]);

  const handleSave = async () => {
    try {
      await save({
        variables: {
          input: {
            candidateId,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: emailVal.trim(),
            phone: phoneVal.trim(),
            companyName: company.trim(),
            jobTitle: title.trim(),
          },
        },
      });
      toast.success('Candidate updated.');
      onSaved();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to update candidate.';
      toast.error(message);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>Edit candidate</ModalHeader>
      <ModalBody>
        <Form onSubmit={(e) => e.preventDefault()}>
          <div className="d-flex" style={{ gap: 12 }}>
            <FormGroup className="flex-fill">
              <Label>First name</Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </FormGroup>
            <FormGroup className="flex-fill">
              <Label>Last name</Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </FormGroup>
          </div>
          <FormGroup>
            <Label>Email</Label>
            <Input
              type="email"
              value={emailVal}
              onChange={(e) => setEmailVal(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>Phone</Label>
            <Input
              value={phoneVal}
              onChange={(e) => setPhoneVal(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>Current company</Label>
            <Input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label>Current title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="link" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSave} disabled={loading}>
          {loading ? 'Saving…' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CandidateQuickEditModal;
